/**
 * Assignment Hub Page
 * Main page for assignment tutoring requests and offers
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Plus, 
  BookOpen, 
  Search,
  SlidersHorizontal, 
  X, 
  Book,
  GraduationCap,
  Info,
  FileQuestion
} from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';
import useAssignments from '../hooks/useAssignments';
import AssignmentCard from '../components/AssignmentCard';
import AssignmentForm from '../components/AssignmentForm';
import { Tab } from '@headlessui/react';

const AssignmentHub = () => {
  const { userProfile, currentUser } = useAuth();
  const { 
    myAssignments, 
    availableAssignments, 
    loading, 
    uploading, 
    loadMyAssignments, 
    loadAvailableAssignments, 
    createAssignment 
  } = useAssignments();
  const [showNewAssignmentForm, setShowNewAssignmentForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    university: '',
    subject: ''
  });

  const location = useLocation();
  
  // Load assignments on component mount
  useEffect(() => {
    if (currentUser) {
      loadMyAssignments();
      loadAvailableAssignments();
    }
  }, [currentUser]);
  
  // Handle tab change based on URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showCreate = params.get('create') === 'true';
    
    if (showCreate) {
      setShowNewAssignmentForm(true);
    }
  }, [location]);
  
  // Handle assignment submission
  const handleSubmitAssignment = async (assignmentData, file) => {
    const success = await createAssignment(assignmentData, file);
    if (success) {
      setShowNewAssignmentForm(false);
    }
  };
  
  // Filter assignments based on search term
  const filterAssignments = (assignments) => {
    if (!searchTerm) return assignments;
    
    const term = searchTerm.toLowerCase();
    return assignments.filter(assignment => 
      assignment.title.toLowerCase().includes(term) ||
      assignment.description.toLowerCase().includes(term) ||
      assignment.subject.toLowerCase().includes(term) ||
      (assignment.course && assignment.course.toLowerCase().includes(term))
    );
  };
  
  // Apply filters to available assignments
  const applyFilters = async () => {
    await loadAvailableAssignments(filters);
    setShowFilters(false);
  };
  
  // Reset filters
  const resetFilters = async () => {
    setFilters({
      university: '',
      subject: ''
    });
    await loadAvailableAssignments({});
    setShowFilters(false);
  };
  
  // Filter assignments
  const filteredMyAssignments = filterAssignments(myAssignments);
  const filteredAvailableAssignments = filterAssignments(availableAssignments);
  
  // Get unique subjects from assignments for filter options
  const getUniqueSubjects = () => {
    const subjects = new Set();
    availableAssignments.forEach(assignment => {
      if (assignment.subject) {
        subjects.add(assignment.subject);
      }
    });
    return Array.from(subjects).sort();
  };
  
  // Get unique universities from assignments for filter options
  const getUniqueUniversities = () => {
    const universities = new Set();
    availableAssignments.forEach(assignment => {
      if (assignment.university) {
        universities.add(assignment.university);
      }
    });
    return Array.from(universities).sort();
  };
  
  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      <header className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <BookOpen className="mr-2 text-green-500" />
            Assignment Hub
          </h1>
          
          <button
            onClick={() => setShowNewAssignmentForm(!showNewAssignmentForm)}
            className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-md hover:from-green-600 hover:to-teal-600 transition shadow-sm"
          >
            {showNewAssignmentForm ? (
              <>
                <X size={18} className="mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus size={18} className="mr-2" />
                Request Help
              </>
            )}
          </button>
        </div>
        
        <p className="text-gray-600">
          Get academic support with your assignments from fellow students.
        </p>
      </header>
      
      {/* New Assignment Form */}
      {showNewAssignmentForm && (
        <div className="mb-8">
          <AssignmentForm 
            onSubmit={handleSubmitAssignment}
            isUploading={uploading}
          />
        </div>
      )}
      
      {/* Academic Integrity Reminder Banner */}
      {!showNewAssignmentForm && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 flex items-start">
          <div className="mr-3 mt-1">
            <Info className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-blue-800">Academic Tutoring Platform</h2>
            <p className="text-sm text-blue-700 mt-1">
              VarsityLink Assignment Hub connects students with tutors for academic support. All tutoring must comply with your institution's academic integrity policy.
            </p>
          </div>
        </div>
      )}
      
      {/* Main Content - Tabs */}
      <div className="mb-6">
<Tab.Group defaultIndex={1}>
  <Tab.List className="flex border-b border-gray-200">
    <Tab
      className={({ selected }) =>
        `py-3 px-4 text-sm font-medium border-b-2 mr-4 focus:outline-none ${
          selected
            ? 'text-green-500 border-green-500'
            : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
        }`
      }
    >
      Help Others
    </Tab>
    <Tab
      className={({ selected }) =>
        `py-3 px-4 text-sm font-medium border-b-2 focus:outline-none ${
          selected
            ? 'text-green-500 border-green-500'
            : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
        }`
      }
    >
      My Assignments
    </Tab>
  </Tab.List>

  {/* Search and Filter Bar */}
  <div className="flex flex-col md:flex-row md:items-center justify-between my-4 gap-3">
    <div className="relative flex-1">
      <Search
        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
        size={18}
      />
      <input
        type="text"
        placeholder="Search assignments..."
        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>

    <button
      onClick={() => setShowFilters(!showFilters)}
      className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
    >
      <SlidersHorizontal size={18} className="mr-2" />
      Filters
    </button>
  </div>

  {/* Filters Panel */}
  {showFilters && (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium text-gray-800">Filter Assignments</h3>
        <button
          onClick={() => setShowFilters(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          <X size={18} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* University Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            University
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            value={filters.university}
            onChange={(e) => setFilters({ ...filters, university: e.target.value })}
          >
            <option value="">All Universities</option>
            {getUniqueUniversities().map((university, index) => (
              <option key={index} value={university}>
                {university}
              </option>
            ))}
          </select>
        </div>

        {/* Subject Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subject
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            value={filters.subject}
            onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
          >
            <option value="">All Subjects</option>
            {getUniqueSubjects().map((subject, index) => (
              <option key={index} value={subject}>
                {subject}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <button
          onClick={resetFilters}
          className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition"
        >
          Reset
        </button>
        <button
          onClick={applyFilters}
          className="px-3 py-1.5 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 transition"
        >
          Apply Filters
        </button>
      </div>
    </div>
  )}

  {/* Tab Content */}
  <Tab.Panels className="mt-4">
    {/* Available Assignments Tab */}
    <Tab.Panel>
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-6 h-6 border-2 border-t-transparent border-green-500 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600">Loading available assignments...</span>
        </div>
      ) : filteredAvailableAssignments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAvailableAssignments.map((assignment) => (
            <AssignmentCard key={assignment.id} assignment={assignment} isOwner={false} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Book className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">No Assignments Available</h3>
          <p className="text-gray-600">
            There are no assignments available for tutoring at the moment. Check back later or
            adjust your filters.
          </p>
        </div>
      )}
    </Tab.Panel>

    {/* My Assignments Tab */}
    <Tab.Panel>
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-6 h-6 border-2 border-t-transparent border-green-500 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600">Loading your assignments...</span>
        </div>
      ) : filteredMyAssignments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMyAssignments.map((assignment) => (
            <AssignmentCard key={assignment.id} assignment={assignment} isOwner={true} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileQuestion className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">No Assignments Found</h3>
          <p className="text-gray-600 mb-4">
            You haven't created any assignment requests yet.
          </p>
          <button
            onClick={() => setShowNewAssignmentForm(true)}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition inline-flex items-center"
          >
            <Plus size={18} className="mr-2" />
            Create Your First Request
          </button>
        </div>
      )}
    </Tab.Panel>
  </Tab.Panels>
</Tab.Group>
      </div>
      
      {/* Benefits Section */}
      <div className="mt-12 bg-gray-50 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">
          Assignment Hub Benefits
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Benefit 1 */}
          <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">Peer Learning</h3>
            <p className="text-gray-600">
              Get help from fellow students who have already mastered the material. Learn through collaboration and discussion.
            </p>
          </div>
          
          {/* Benefit 2 */}
          <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <GraduationCap className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">Academic Support</h3>
            <p className="text-gray-600">
              Receive guidance on complex assignments and improve your understanding of difficult concepts through one-on-one help.
            </p>
          </div>
          
          {/* Benefit 3 */}
          <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <Book className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">Knowledge Sharing</h3>
            <p className="text-gray-600">
              Share your expertise by tutoring others. Reinforce your own understanding while helping fellow students succeed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentHub;